'use client';

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { Chip } from 'src/components';

/**
 * Interface for a single list item
 */
interface ListItem {
  id: string | number;
  name: string;
}

const DROPDOWN_WIDTH = 192; // w-48
const DROPDOWN_MAX_HEIGHT = 256; // max-h-60 list (240) + py-2 (16)
const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Props for the ResultsItemLists component
 */
interface ResultsItemListsProps {
  lists: ListItem[];
}

/**
 * ResultsItemLists - Displays provider list chips with responsive container queries
 *
 * Shows a subset of list chips based on available container width:
 * - Default (< 384px): 1 chip + "+more" button
 * - @sm (384px+): 2 chips + "+more" button
 * - @md (448px+): 3 chips + "+more" button
 *
 * The "+more" button opens a dropdown showing remaining lists.
 *
 * @param props - Component props
 * @returns React element displaying list chips with responsive behavior
 *
 * @example
 * ```tsx
 * <ResultsItemLists lists={[
 *   { id: '1', name: 'Test List' },
 *   { id: '2', name: 'Another List' }
 * ]} />
 * ```
 */
const ResultsItemLists: React.FC<ResultsItemListsProps> = ({ lists }): React.ReactElement => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    isAbove: false,
  });
  const [activeHiddenLists, setActiveHiddenLists] = useState<ListItem[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setActiveHiddenLists([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Renders a single list chip
   * @param list - The list item to render
   * @param isOnlyChip - Whether this is the only visible chip (allows wider width)
   * @returns JSX element for the list chip
   */
  const renderListChip = (list: ListItem, isOnlyChip = false): React.JSX.Element => (
    <Link
      key={list.id}
      href={`/lists/my/${list.id}`}
      scroll={false}
      replace={false}
      prefetch={false}
      onClick={(e) => {
        e.stopPropagation();
      }}
      data-testid={`results-item-list-link-${list.id}`}
      className="flex-shrink-0"
    >
      <div
        data-testid={`results-item-list-chip-${list.id}`}
        className={`${isOnlyChip ? 'max-w-48' : 'max-w-28'} px-2 py-1 bg-neutral-100 rounded-2xl outline outline-1 outline-offset-[-1px] outline-neutral-200 overflow-hidden`}
      >
        <div className="text-tertiary text-xs font-medium font-['Inter'] leading-tight text-center truncate">
          {list.name}
        </div>
      </div>
    </Link>
  );

  /**
   * Renders the "+more" button with dropdown functionality
   * @param count - Number of hidden lists
   * @param hiddenLists - Array of list items to show in dropdown
   * @returns JSX element for the "+more" button and dropdown
   */
  const renderMoreButton = (count: number, hiddenLists: ListItem[]): React.JSX.Element => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.currentTarget;
      if (!isDropdownOpen && target) {
        const rect = target.getBoundingClientRect();
        // Flip above the trigger when the menu would run off the bottom of the viewport
        // and there is more room above (so when neither side fits, the roomier one
        // wins), and keep it inside the viewport horizontally, so every hidden list
        // stays reachable.
        const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
        const spaceAbove = rect.top - DROPDOWN_GAP;
        const opensAbove =
          spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
        const maxLeft = window.innerWidth - DROPDOWN_WIDTH - VIEWPORT_MARGIN;
        setDropdownPosition({
          top: opensAbove ? rect.top - DROPDOWN_GAP : rect.bottom + DROPDOWN_GAP,
          left: Math.max(
            VIEWPORT_MARGIN,
            Math.min(rect.right - DROPDOWN_WIDTH, maxLeft)
          ),
          isAbove: opensAbove,
        });
        setActiveHiddenLists(hiddenLists);
        setIsDropdownOpen(true);
        triggerRef.current = target;
      } else {
        setIsDropdownOpen(false);
        setActiveHiddenLists([]);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Escape' && isDropdownOpen) {
        e.preventDefault();
        setIsDropdownOpen(false);
        setActiveHiddenLists([]);
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
        aria-label={`Show ${count} more list${count !== 1 ? 's' : ''}`}
        className="relative flex-shrink-0 border-0 bg-transparent p-0 cursor-pointer"
      >
        <Chip
          data-testid="results-item-remaining-chip"
          size="sm"
          variant="outlined"
          className="pointer-events-none hover:bg-neutral-200 transition-colors duration-200 text-sm bg-gray-100 text-gray-600 border-gray-200"
        >
          +{count}
        </Chip>
        {isDropdownOpen &&
          activeHiddenLists.length > 0 &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed w-48 bg-white rounded-lg shadow-lg border border-neutral-200 py-2 z-[70]"
              style={{
                ...(dropdownPosition.isAbove
                  ? { bottom: window.innerHeight - dropdownPosition.top }
                  : { top: dropdownPosition.top }),
                left: dropdownPosition.left,
              }}
            >
              <div className="max-h-60 overflow-y-auto">
                {activeHiddenLists.map((list) => (
                  <Link
                    key={list.id}
                    href={`/lists/my/${list.id}`}
                    scroll={false}
                    replace={false}
                    prefetch={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(false);
                      setActiveHiddenLists([]);
                    }}
                    className="block"
                    data-testid={`results-item-dropdown-link-${list.id}`}
                  >
                    <div className="px-4 py-2 hover:bg-neutral-50 cursor-pointer text-sm text-neutral-700">
                      {list.name}
                    </div>
                  </Link>
                ))}
              </div>
            </div>,
            document.body
          )}
      </button>
    );
  };

  if (lists.length === 0) return null;

  return (
    <>
      {/* Show 1 chip by default (smallest width) */}
      <div className="flex items-center gap-1 relative w-full @sm:hidden">
        {renderListChip(lists[0], lists.length === 1)}
        {lists.length > 1 && renderMoreButton(lists.length - 1, lists.slice(1))}
      </div>

      {/* Show 2 chips at @sm (384px container width) */}
      <div className="hidden @sm:flex @md:hidden items-center gap-1 relative w-full">
        {lists.slice(0, 2).map((list) => renderListChip(list, lists.length === 1))}
        {lists.length > 2 && renderMoreButton(lists.length - 2, lists.slice(2))}
      </div>

      {/* Show 3 chips at @md+ (448px+ container width) */}
      <div className="hidden @md:flex items-center gap-1 relative w-full">
        {lists.slice(0, 3).map((list) => renderListChip(list, lists.length === 1))}
        {lists.length > 3 && renderMoreButton(lists.length - 3, lists.slice(3))}
      </div>
    </>
  );
};

export default ResultsItemLists;
